import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import {
  getEpoMailTemplates,
  getEpoMailTokens,
  saveEpoMailTemplate,
  setEpoMailTemplateActive,
  setEpoMailTestMode,
} from "../../../services/admin";

const get = (obj, camelKey, pascalKey, fallback = null) => {
  if (!obj) return fallback;

  if (Object.prototype.hasOwnProperty.call(obj, camelKey)) {
    return obj[camelKey];
  }

  if (Object.prototype.hasOwnProperty.call(obj, pascalKey)) {
    return obj[pascalKey];
  }

  return fallback;
};

const list = (value) => (Array.isArray(value) ? value : []);

const normalizeText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const getErrorMessage = (error, fallback = "Operation failed.") => {
  const data = error?.response?.data;

  if (typeof data === "string") {
    return data;
  }

  return data?.message || data?.Message || error?.message || fallback;
};

const unwrapRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.result)) {
    return data.result;
  }

  if (Array.isArray(data?.Result)) {
    return data.Result;
  }

  if (data && typeof data === "object") {
    return [data];
  }

  return [];
};

const GENERIC_CC_EVENTS = new Set([
  "EPO_SUBMITTED_REQUESTER",
  "EPO_SELF_APPROVED",
  "EPO_FULLY_APPROVED",
  "EPO_FULLY_DENIED",
  "EPO_REAPPROVAL_SELF_APPROVED",
]);

const EPO_CC_PERSON_REGEX = /\{\{EPO_CC_PERSON:(\d+)\}\}/gi;

const extractEpoCcPersonIds = (expression) => {
  const value = String(expression || "");
  const ids = [];

  for (const match of value.matchAll(EPO_CC_PERSON_REGEX)) {
    const personId = normalizeText(match[1]);

    if (personId) {
      ids.push(personId);
    }
  }

  return [...new Set(ids)];
};

const removeEpoCcPersonMarkers = (expression) =>
  normalizeText(
    String(expression || "")
      .replace(EPO_CC_PERSON_REGEX, "")
      .replace(/,{2,}/g, ",")
      .replace(/^,|,$/g, ""),
  );

const buildEpoCcPersonMarkers = (employees) =>
  employees
    .map((employee) =>
      normalizeText(employee?.personId ?? employee?.employeeCode ?? ""),
    )
    .filter(Boolean)
    .map((personId) => `{{EPO_CC_PERSON:${personId}}}`);

const EVENT_OPTIONS = [
  {
    value: "EPO_SUBMITTED_REQUESTER",
    label: "EPO Submitted - Requester",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_SELF_APPROVED",
    label: "EPO Self Approved",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_APPROVAL_REQUIRED",
    label: "Approval Required",
    recipient: "{{ApproverEmail}}",
  },
  {
    value: "EPO_APPROVAL_ACTION_APPROVED",
    label: "Approval Action - Approved",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_APPROVAL_ACTION_DENIED",
    label: "Approval Action - Denied",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_FULLY_APPROVED",
    label: "EPO Fully Approved",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_FULLY_DENIED",
    label: "EPO Fully Denied",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_DETAILS_EDITED",
    label: "EPO Details Edited",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_REAPPROVAL_SUBMITTED_REQUESTER",
    label: "Amount Changed - Submitted for Re-approval",
    recipient: "{{RequesterEmail}}",
  },
  {
    value: "EPO_REAPPROVAL_REQUIRED",
    label: "Amount Changed - Approval Required",
    recipient: "{{ApproverEmail}}",
  },
  {
    value: "EPO_REAPPROVAL_SELF_APPROVED",
    label: "Amount Changed - Self Approved",
    recipient: "{{RequesterEmail}}",
  },
];

const STEP_OPTIONS = [
  "SELF",
  "DEPUTY",
  "MPC",
  "HIGHER_AUTHORITY",
  "PRESIDENT",
  "IT_APPROVER",
  "SPECIFIC_USER",
];

const emptyTemplate = {
  id: null,

  templateCode: "",
  templateName: "",
  eventCode: "EPO_APPROVAL_REQUIRED",

  workflowRuleCode: "",
  workflowStepCode: "",

  subjectTemplate: "EPO #{{EpoId}} - {{CurrentStepName}} Approval Required",

  bodyHtml: `<div style="font-family:Arial,sans-serif;">
  <p>Dear {{ApproverName}},</p>

  <p>
    EPO <strong>#{{EpoId}}</strong>
    requires your approval.
  </p>

  <p>
    <strong>Requested By:</strong>
    {{RequesterName}}
  </p>

  <p>
    <strong>Amount:</strong>
    {{Amount}} {{Currency}}
  </p>

  <p>
    <strong>Purpose:</strong>
    {{Purpose}}
  </p>

  <p>
    <strong>Approval Stage:</strong>
    {{CurrentStepName}}
  </p>

  <p>
    <a href="{{ApproveUrl}}">Approve</a>
    &nbsp;
    <a href="{{DenyUrl}}">Deny</a>
  </p>

  <p>
    Regards,<br/>
    {{CompanyName}}
  </p>
</div>`,

  toExpression: "{{ApproverEmail}}",
  ccExpression: "",
  bccExpression: "",

  includePdf: true,
  approvalBaseUrl: "",

  priority: 100,

  isTestMode: true,
  testToEmails: "jay@sswhite.net",
  testCcEmails: "",
  testBccEmails: "",

  isActive: true,
};

const templateFromRow = (row) => ({
  id: get(row, "id", "Id", null),

  templateCode: get(row, "templateCode", "TemplateCode", ""),

  templateName: get(row, "templateName", "TemplateName", ""),

  eventCode: get(row, "eventCode", "EventCode", ""),

  workflowRuleCode: get(row, "workflowRuleCode", "WorkflowRuleCode", "") || "",

  workflowStepCode: get(row, "workflowStepCode", "WorkflowStepCode", "") || "",

  subjectTemplate: get(row, "subjectTemplate", "SubjectTemplate", ""),

  bodyHtml: get(row, "bodyHtml", "BodyHtml", ""),

  toExpression: get(row, "toExpression", "ToExpression", "") || "",

  ccExpression: get(row, "ccExpression", "CcExpression", "") || "",

  bccExpression: get(row, "bccExpression", "BccExpression", "") || "",

  includePdf: Boolean(get(row, "includePdf", "IncludePdf", false)),

  approvalBaseUrl: get(row, "approvalBaseUrl", "ApprovalBaseUrl", "") || "",

  priority: Number(get(row, "priority", "Priority", 100)),

  isTestMode: Boolean(get(row, "isTestMode", "IsTestMode", true)),

  testToEmails: get(row, "testToEmails", "TestToEmails", "") || "",

  testCcEmails: get(row, "testCcEmails", "TestCcEmails", "") || "",

  testBccEmails: get(row, "testBccEmails", "TestBccEmails", "") || "",

  isActive: Boolean(get(row, "isActive", "IsActive", true)),
});

const renderSample = (value) => {
  let result = String(value || "");

  const replacements = {
    "{{EpoId}}": "10025",

    "{{RequesterName}}": "Test Requester",
    "{{RequesterEmail}}": "requester@company.com",

    "{{ApproverName}}": "Test Approver",
    "{{ApproverEmail}}": "approver@company.com",
    "{{ApproverUserId}}": "1234",

    "{{CurrentStepCode}}": "HIGHER_AUTHORITY",
    "{{CurrentStepName}}": "Higher Authority",

    "{{NextStepCode}}": "PRESIDENT",
    "{{NextStepName}}": "President",

    "{{Amount}}": "125,000",
    "{{Currency}}": "INR",

    "{{Purpose}}": "Test Purchase",

    "{{ApprovalMessage}}": "EPO is pending approval",

    "{{StatusText}}": "Pending",

    "{{ApproveUrl}}": "#preview-approve",

    "{{DenyUrl}}": "#preview-deny",

    "{{CompanyName}}": "SS WHITE TECHNOLOGY PVT. LTD.",

    "{{AwaitingForApproval}}": "Higher Authority",

    "{{LogicalToEmails}}": "approver@company.com",

    "{{LogicalCcEmails}}": "requester@company.com",

    "{{EditNotificationCcEmails}}": "approver1@company.com, deputy@company.com",

    "{{RequesterSubmissionCcEmails}}": "deputy@company.com",

    "{{SelfApprovedCcEmails}}": "deputy@company.com",

    "{{FinalApprovalCcEmails}}": "approver@company.com",

    "{{ChangedDetailsHtml}}": `
  <table style="width:100%;border-collapse:collapse">
    <tr>
      <td style="padding:8px;border:1px solid #ddd"><strong>Field</strong></td>
      <td style="padding:8px;border:1px solid #ddd"><strong>Old</strong></td>
      <td style="padding:8px;border:1px solid #ddd"><strong>New</strong></td>
    </tr>
    <tr>
      <td style="padding:8px;border:1px solid #ddd">Purpose</td>
      <td style="padding:8px;border:1px solid #ddd">Old Purpose</td>
      <td style="padding:8px;border:1px solid #ddd">Updated Purpose</td>
    </tr>
  </table>
`,
  };

  Object.entries(replacements).forEach(([token, replacement]) => {
    result = result.split(token).join(replacement);
  });

  return result;
};

const EMPTY_EMPLOYEES = [];

const filterEmployees = (options, state) => {
  const terms = normalizeText(state?.inputValue)
    .toLowerCase()
    .split(" ")
    .filter(Boolean);

  if (!terms.length) {
    return options;
  }

  return options.filter((option) => {
    const searchable = normalizeText(
      `${option?.displayName || ""} ${option?.fullName || ""} ${
        option?.employeeCode || ""
      } ${option?.personId || ""}`,
    ).toLowerCase();

    return terms.every((term) => searchable.includes(term));
  });
};

const IT_PURCHASE_CC_PERSON_REGEX = /\{\{IT_PURCHASE_CC_PERSON:(\d+)\}\}/gi;
const IT_PURCHASE_CC_EMAIL_REGEX = /\{\{IT_PURCHASE_CC:([^{}]+)\}\}/gi;

const SUBMISSION_EVENTS = new Set([
  "EPO_SUBMITTED_REQUESTER",
  "EPO_REAPPROVAL_SUBMITTED_REQUESTER",
]);

const extractItPurchaseCcPersonIds = (expression) => {
  const value = String(expression || "");
  const personIds = [];

  for (const match of value.matchAll(IT_PURCHASE_CC_PERSON_REGEX)) {
    const personId = normalizeText(match[1]);
    if (personId) {
      personIds.push(personId);
    }
  }

  return [...new Set(personIds)];
};

const removeItPurchaseCcMarkers = (expression) =>
  normalizeText(
    String(expression || "")
      .replace(IT_PURCHASE_CC_PERSON_REGEX, "")
      .replace(/,{2,}/g, ",")
      .replace(/^,|,$/g, ""),
  );

const buildItPurchaseCcMarkers = (employees) =>
  employees
    .map((employee) =>
      normalizeText(employee?.personId ?? employee?.employeeCode ?? ""),
    )
    .filter(Boolean)
    .map((personId) => `{{IT_PURCHASE_CC_PERSON:${personId}}}`);

const TemplateDialog = ({
  open,
  template,
  rules,
  employees,
  availableTokens,
  saving,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(emptyTemplate);
  const [preview, setPreview] = useState(false);

  const fieldRefs = {
    subjectTemplate: useRef(null),
    bodyHtml: useRef(null),
    toExpression: useRef(null),
    ccExpression: useRef(null),
    bccExpression: useRef(null),
  };

  const dialogContentRef = useRef(null);
  const dragScrollRef = useRef(null);

  const [ccEmployees, setCcEmployees] = useState([]);
  const [genericCcEmployees, setGenericCcEmployees] = useState([]);

  const employeeOptions = Array.isArray(employees)
    ? employees
    : EMPTY_EMPLOYEES;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (template) {
      const loaded = templateFromRow(template);

      const itPurchasePersonIds = extractItPurchaseCcPersonIds(
        loaded.ccExpression,
      );

      const genericPersonIds = extractEpoCcPersonIds(loaded.ccExpression);

      const selectedItPurchaseEmployees = employeeOptions.filter((employee) =>
        itPurchasePersonIds.includes(
          normalizeText(employee?.personId ?? employee?.employeeCode ?? ""),
        ),
      );

      const selectedGenericEmployees = employeeOptions.filter((employee) =>
        genericPersonIds.includes(
          normalizeText(employee?.personId ?? employee?.employeeCode ?? ""),
        ),
      );

      setCcEmployees(selectedItPurchaseEmployees);
      setGenericCcEmployees(selectedGenericEmployees);

      setForm({
        ...loaded,
        ccExpression: removeItPurchaseCcMarkers(
          removeEpoCcPersonMarkers(loaded.ccExpression),
        ),
      });
    } else {
      setCcEmployees([]);
      setGenericCcEmployees([]);

      setForm({
        ...emptyTemplate,
      });
    }

    setPreview(false);
  }, [open, template, employeeOptions]);

  const change = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const selectedEvent = EVENT_OPTIONS.find(
    (item) => item.value === form.eventCode,
  );

  const isApprovalRequired = [
    "EPO_APPROVAL_REQUIRED",
    "EPO_REAPPROVAL_REQUIRED",
  ].includes(form.eventCode);

  const isSubmissionEvent = SUBMISSION_EVENTS.has(form.eventCode);

  const isGenericCcEvent = GENERIC_CC_EVENTS.has(form.eventCode);

  const changeEvent = (eventCode) => {
    const eventConfig = EVENT_OPTIONS.find((item) => item.value === eventCode);

    setForm((current) => ({
      ...current,

      eventCode,

      /*
       * IMPORTANT:
       * Approval recipient is NEVER manually typed.
       */
      toExpression: eventConfig?.recipient || current.toExpression,
    }));
  };

  const insertToken = (
    fieldName,
    token,
    startOverride = null,
    endOverride = null,
  ) => {
    const input = fieldRefs[fieldName]?.current;

    setForm((current) => {
      const value = current[fieldName] || "";

      const start =
        startOverride ??
        (typeof input?.selectionStart === "number"
          ? input.selectionStart
          : value.length);

      const end =
        endOverride ??
        (typeof input?.selectionEnd === "number" ? input.selectionEnd : start);

      return {
        ...current,
        [fieldName]: value.slice(0, start) + token + value.slice(end),
      };
    });

    const start =
      startOverride ??
      (typeof input?.selectionStart === "number"
        ? input.selectionStart
        : (input?.value || "").length);

    requestAnimationFrame(() => {
      const target = fieldRefs[fieldName]?.current;

      if (!target) return;

      const cursorPosition = start + token.length;

      target.focus();
      target.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleTokenDragStart = (event, token) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/x-epo-token", token);

    // Fallback for browsers
    event.dataTransfer.setData("text/plain", token);
  };

  const getCaretIndexFromPoint = (input, clientX, clientY) => {
    if (!input) return 0;

    const value = input.value || "";

    // Create a hidden mirror of the textarea
    const mirror = document.createElement("div");
    const computed = window.getComputedStyle(input);

    const rect = input.getBoundingClientRect();

    mirror.style.position = "fixed";
    mirror.style.left = `${rect.left}px`;
    mirror.style.top = `${rect.top}px`;
    mirror.style.width = `${rect.width}px`;
    mirror.style.height = `${rect.height}px`;

    mirror.style.visibility = "hidden";
    mirror.style.pointerEvents = "none";

    mirror.style.boxSizing = computed.boxSizing;
    mirror.style.padding = computed.padding;
    mirror.style.border = computed.border;
    mirror.style.font = computed.font;
    mirror.style.fontFamily = computed.fontFamily;
    mirror.style.fontSize = computed.fontSize;
    mirror.style.fontWeight = computed.fontWeight;
    mirror.style.lineHeight = computed.lineHeight;
    mirror.style.letterSpacing = computed.letterSpacing;
    mirror.style.textAlign = computed.textAlign;
    mirror.style.textTransform = computed.textTransform;

    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.wordWrap = "break-word";
    mirror.style.overflowWrap = "break-word";

    document.body.appendChild(mirror);

    const fragment = document.createDocumentFragment();
    const spans = [];

    for (let i = 0; i < value.length; i++) {
      const span = document.createElement("span");
      span.textContent = value[i];

      // Preserve spaces/newlines
      if (value[i] === " ") {
        span.textContent = "\u00a0";
      } else if (value[i] === "\n") {
        span.innerHTML = "<br/>";
      }

      fragment.appendChild(span);
      spans.push(span);
    }

    // Empty value
    if (!value.length) {
      const span = document.createElement("span");
      span.textContent = "\u200b";
      fragment.appendChild(span);
      spans.push(span);
    }

    mirror.appendChild(fragment);

    let bestIndex = value.length;
    let bestDistance = Infinity;

    /*
     * The textarea itself can be internally scrolled.
     * Adjust the mouse position into the mirror's coordinate system.
     */
    const scrollLeft = input.scrollLeft || 0;
    const scrollTop = input.scrollTop || 0;

    const targetX = clientX + scrollLeft;
    const targetY = clientY + scrollTop;

    spans.forEach((span, index) => {
      const spanRect = span.getBoundingClientRect();

      const centerX = spanRect.left + spanRect.width / 2;
      const centerY = spanRect.top + spanRect.height / 2;

      const x = clientX - centerX;
      const y = clientY - centerY;

      const distance = x * x + y * y;

      if (distance < bestDistance) {
        bestDistance = distance;

        /*
         * If dropping on the right half of a character,
         * insert after it. Otherwise insert before it.
         */
        if (clientX >= spanRect.left + spanRect.width / 2) {
          bestIndex = index + 1;
        } else {
          bestIndex = index;
        }
      }
    });

    document.body.removeChild(mirror);

    return Math.max(0, Math.min(bestIndex, value.length));
  };

  const handleTokenDrop = (event, fieldName) => {
    event.preventDefault();

    const token =
      event.dataTransfer.getData("application/x-epo-token") ||
      event.dataTransfer.getData("text/plain");

    if (!token) {
      return;
    }

    const input = fieldRefs[fieldName]?.current;

    if (!input) {
      return;
    }

    const caretPosition = getCaretIndexFromPoint(
      input,
      event.clientX,
      event.clientY,
    );

    insertToken(fieldName, token, caretPosition, caretPosition);
  };

  const stopDragAutoScroll = () => {
    if (dragScrollRef.current?.animationFrame) {
      cancelAnimationFrame(dragScrollRef.current.animationFrame);
    }

    dragScrollRef.current = null;
  };

  const handleTokenDragEnd = () => {
    stopDragAutoScroll();
  };

  const handleTokenDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";

    const container = dialogContentRef.current;

    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();

    const mouseY = event.clientY;

    const edgeSize = 90;
    const maxSpeed = 18;

    let speed = 0;

    // Scroll UP when dragging near the top
    if (mouseY < rect.top + edgeSize) {
      const distance = mouseY - rect.top;

      speed = -Math.max(4, maxSpeed * (1 - Math.max(distance, 0) / edgeSize));
    }

    // Scroll DOWN when dragging near the bottom
    else if (mouseY > rect.bottom - edgeSize) {
      const distance = rect.bottom - mouseY;

      speed = Math.max(4, maxSpeed * (1 - Math.max(distance, 0) / edgeSize));
    }

    if (speed !== 0) {
      if (!dragScrollRef.current) {
        dragScrollRef.current = {
          speed,
          animationFrame: null,
        };
      }

      dragScrollRef.current.speed = speed;

      if (!dragScrollRef.current.animationFrame) {
        const scroll = () => {
          const current = dragScrollRef.current;

          if (!current || !dialogContentRef.current) {
            return;
          }

          dialogContentRef.current.scrollTop += current.speed;

          current.animationFrame = requestAnimationFrame(scroll);
        };

        dragScrollRef.current.animationFrame = requestAnimationFrame(scroll);
      }
    } else {
      stopDragAutoScroll();
    }
  };

  const valid =
    normalizeText(form.templateCode) &&
    normalizeText(form.templateName) &&
    normalizeText(form.eventCode) &&
    normalizeText(form.subjectTemplate) &&
    normalizeText(form.bodyHtml) &&
    normalizeText(form.toExpression) &&
    Number(form.priority) > 0;

  const configuredItPurchaseMarkers = isSubmissionEvent
    ? buildItPurchaseCcMarkers(ccEmployees)
    : [];

  const configuredGenericCcMarkers = isGenericCcEvent
    ? buildEpoCcPersonMarkers(genericCcEmployees)
    : [];

  const finalCcExpression = normalizeText(
    [
      normalizeText(form.ccExpression),
      ...configuredItPurchaseMarkers,
      ...configuredGenericCcMarkers,
    ]
      .filter(Boolean)
      .join(","),
  );

  const payload = {
    id: form.id,

    templateCode: normalizeText(form.templateCode).toUpperCase(),

    templateName: normalizeText(form.templateName),

    eventCode: normalizeText(form.eventCode).toUpperCase(),

    workflowRuleCode: normalizeText(form.workflowRuleCode) || null,

    workflowStepCode: normalizeText(form.workflowStepCode) || null,

    subjectTemplate: form.subjectTemplate,

    bodyHtml: form.bodyHtml,

    /*
     * Safety:
     * Approval mail always uses verified
     * runtime ApproverEmail.
     */
    toExpression: isApprovalRequired ? "{{ApproverEmail}}" : form.toExpression,

    ccExpression: finalCcExpression || null,

    bccExpression: normalizeText(form.bccExpression) || null,

    includePdf: Boolean(form.includePdf),

    approvalBaseUrl: normalizeText(form.approvalBaseUrl) || null,

    priority: Number(form.priority),

    isTestMode: Boolean(form.isTestMode),

    testToEmails: normalizeText(form.testToEmails) || null,

    testCcEmails: normalizeText(form.testCcEmails) || null,

    testBccEmails: normalizeText(form.testBccEmails) || null,

    isActive: Boolean(form.isActive),
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        {template ? "Edit EPO Email Template" : "Add EPO Email Template"}
      </DialogTitle>

      <DialogContent
        ref={dialogContentRef}
        dividers
        sx={{
          position: "relative",
        }}
      >
        <Stack spacing={2}>
          {isApprovalRequired ? (
            <Alert severity="warning">
              Approval-required emails are recipient protected. The TO address
              is always resolved from the verified workflow approver. A static
              email address cannot be configured here.
            </Alert>
          ) : null}

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Template Code"
                value={form.templateCode}
                disabled={Boolean(form.id)}
                onChange={(event) =>
                  change(
                    "templateCode",
                    event.target.value.toUpperCase().replace(/\s+/g, "_"),
                  )
                }
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Template Name"
                value={form.templateName}
                onChange={(event) => change("templateName", event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={form.priority}
                inputProps={{
                  min: 1,
                }}
                onChange={(event) =>
                  change("priority", Number(event.target.value))
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Mail Event"
                value={form.eventCode}
                onChange={(event) => changeEvent(event.target.value)}
              >
                {EVENT_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Workflow Rule Override"
                value={form.workflowRuleCode}
                onChange={(event) =>
                  change("workflowRuleCode", event.target.value)
                }
              >
                <MenuItem value="">Generic / All Rules</MenuItem>

                {list(rules).map((rule) => {
                  const code = get(rule, "ruleCode", "RuleCode", "");

                  return (
                    <MenuItem key={code} value={code}>
                      {code} - {get(rule, "ruleName", "RuleName", "")}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Workflow Step Override"
                value={form.workflowStepCode}
                onChange={(event) =>
                  change("workflowStepCode", event.target.value)
                }
              >
                <MenuItem value="">Generic / All Steps</MenuItem>

                {STEP_OPTIONS.map((step) => (
                  <MenuItem key={step} value={step}>
                    {step}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider />

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 1,
              }}
            >
              Available Tokens
            </Typography>

            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {availableTokens.map((item) => (
                <Tooltip
                  key={item.token}
                  title={
                    item.description
                      ? `${item.displayName}: ${item.description}`
                      : `Insert ${item.displayName}`
                  }
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={item.token}
                    draggable
                    onDragStart={(event) =>
                      handleTokenDragStart(event, item.token)
                    }
                    onDragEnd={handleTokenDragEnd}
                    onClick={() => insertToken("bodyHtml", item.token)}
                    sx={{
                      cursor: "grab",

                      "&:active": {
                        cursor: "grabbing",
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>

          <TextField
            fullWidth
            label="Email Subject"
            value={form.subjectTemplate}
            inputRef={fieldRefs.subjectTemplate}
            onChange={(event) => change("subjectTemplate", event.target.value)}
            onDragOver={handleTokenDragOver}
            onDrop={(event) => handleTokenDrop(event, "subjectTemplate")}
          />

          <TextField
            fullWidth
            multiline
            minRows={14}
            label="Email Body HTML"
            value={form.bodyHtml}
            inputRef={fieldRefs.bodyHtml}
            onChange={(event) => change("bodyHtml", event.target.value)}
            onDragOver={handleTokenDragOver}
            onDrop={(event) => handleTokenDrop(event, "bodyHtml")}
            inputProps={{
              style: {
                fontFamily: "monospace",
                fontSize: 13,
              },
            }}
          />

          <Grid container spacing={1.5} alignItems="flex-start">
            {/* TO */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="TO"
                value={
                  isApprovalRequired ? "{{ApproverEmail}}" : form.toExpression
                }
                disabled={isApprovalRequired}
                onChange={(event) => change("toExpression", event.target.value)}
                helperText={
                  isApprovalRequired
                    ? "Protected: resolved from the actual workflow approver."
                    : "Token or recipient expression."
                }
              />
            </Grid>

            {/* CC */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="CC"
                value={form.ccExpression}
                inputRef={fieldRefs.ccExpression}
                onChange={(event) => change("ccExpression", event.target.value)}
                onDragOver={handleTokenDragOver}
                onDrop={(event) => handleTokenDrop(event, "ccExpression")}
                placeholder="{{RequesterSubmissionCcEmails}}"
                helperText={
                  isSubmissionEvent
                    ? "Normal/dynamic CC expression."
                    : "Normal CC expression."
                }
              />
            </Grid>

            {/* BCC */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="BCC"
                value={form.bccExpression}
                inputRef={fieldRefs.bccExpression}
                onChange={(event) =>
                  change("bccExpression", event.target.value)
                }
                onDragOver={handleTokenDragOver}
                onDrop={(event) => handleTokenDrop(event, "bccExpression")}
                helperText="Optional BCC expression."
              />
            </Grid>

            {/* IT Purchase CC */}
            {isSubmissionEvent ? (
              <Grid item xs={12} md={4}>
                <Autocomplete
                  multiple
                  size="small"
                  options={employeeOptions}
                  value={ccEmployees}
                  onChange={(_, values) => {
                    setCcEmployees(values);
                  }}
                  filterOptions={filterEmployees}
                  getOptionLabel={(option) =>
                    `${option?.displayName || option?.fullName || ""} (${
                      option?.personId || ""
                    })`
                  }
                  isOptionEqualToValue={(option, value) =>
                    String(option?.personId ?? "") ===
                    String(value?.personId ?? "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="IT Purchase CC"
                      placeholder="Select employee(s)"
                      helperText="Additional CC for IT Purchase."
                    />
                  )}
                />
              </Grid>
            ) : null}

            {/* Notification CC */}
            {isGenericCcEvent ? (
              <Grid item xs={12} md={4}>
                <Autocomplete
                  multiple
                  size="small"
                  options={employeeOptions}
                  value={genericCcEmployees}
                  onChange={(_, values) => {
                    setGenericCcEmployees(values);
                  }}
                  filterOptions={filterEmployees}
                  getOptionLabel={(option) =>
                    `${option?.displayName || option?.fullName || ""} (${
                      option?.personId || ""
                    })`
                  }
                  isOptionEqualToValue={(option, value) =>
                    String(option?.personId ?? "") ===
                    String(value?.personId ?? "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Notification CC"
                      placeholder="Select employee(s)"
                      helperText="Employees receiving this notification in CC."
                    />
                  )}
                />
              </Grid>
            ) : null}
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.includePdf)}
                    onChange={(event) =>
                      change("includePdf", event.target.checked)
                    }
                  />
                }
                label="Include EPO PDF"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.isActive)}
                    onChange={(event) =>
                      change("isActive", event.target.checked)
                    }
                  />
                }
                label="Template Active"
              />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={<VisibilityIcon />}
              onClick={() => setPreview((value) => !value)}
            >
              {preview ? "Hide Preview" : "Preview"}
            </Button>
          </Stack>

          {preview ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Subject
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                }}
              >
                {renderSample(form.subjectTemplate)}
              </Typography>

              <Divider
                sx={{
                  mb: 2,
                }}
              />

              <Box
                dangerouslySetInnerHTML={{
                  __html: renderSample(form.bodyHtml),
                }}
              />
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={saving || !valid}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={() => onSave(payload)}
        >
          Save Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EpoMailTemplateManager = ({
  rules = [],
  employees = EMPTY_EMPLOYEES,
}) => {
  const [templates, setTemplates] = useState([]);

  const [mailTokens, setMailTokens] = useState([]);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);

  const [editingTemplate, setEditingTemplate] = useState(null);

  const [testMode, setTestMode] = useState(true);

  const [testToEmails, setTestToEmails] = useState("jay@sswhite.net");

  const [testCcEmails, setTestCcEmails] = useState("");

  const [inactiveExpanded, setInactiveExpanded] = useState(false);

  const loadMailTokens = useCallback(async () => {
    try {
      const response = await getEpoMailTokens();

      const rows = unwrapRows(response);

      const activeTokens = rows
        .filter((row) => Boolean(get(row, "isActive", "IsActive", true)))
        .map((row) => ({
          token: get(row, "tokenKey", "TokenKey", ""),
          displayName: get(row, "displayName", "DisplayName", ""),
          description: get(row, "description", "Description", ""),
          category: get(row, "category", "Category", ""),
          formatType: get(row, "formatType", "FormatType", "TEXT"),
          sortOrder: Number(get(row, "sortOrder", "SortOrder", 100)),
        }))
        .filter((item) => item.token);

      activeTokens.sort(
        (a, b) =>
          a.sortOrder - b.sortOrder ||
          a.displayName.localeCompare(b.displayName),
      );

      setMailTokens(activeTokens);
    } catch (tokenError) {
      console.error("Unable to load EPO mail tokens.", tokenError);
      setMailTokens([]);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getEpoMailTemplates();

      const rows = unwrapRows(response);

      setTemplates(rows);

      if (rows.length) {
        const first = rows[0];

        setTestMode(Boolean(get(first, "isTestMode", "IsTestMode", true)));

        setTestToEmails(
          get(first, "testToEmails", "TestToEmails", "jay@sswhite.net") ||
            "jay@sswhite.net",
        );

        setTestCcEmails(get(first, "testCcEmails", "TestCcEmails", "") || "");
      }
    } catch (loadError) {
      setError(
        getErrorMessage(loadError, "Unable to load EPO email templates."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadMailTokens();
  }, [loadTemplates, loadMailTokens]);

  const availableTokens = useMemo(() => mailTokens, [mailTokens]);

  const sortedTemplates = useMemo(
    () =>
      [...templates].sort((a, b) => {
        const eventCompare = String(
          get(a, "eventCode", "EventCode", ""),
        ).localeCompare(String(get(b, "eventCode", "EventCode", "")));

        if (eventCompare !== 0) {
          return eventCompare;
        }

        return (
          Number(get(a, "priority", "Priority", 100)) -
          Number(get(b, "priority", "Priority", 100))
        );
      }),
    [templates],
  );

  const activeTemplates = useMemo(
    () =>
      sortedTemplates.filter((row) =>
        Boolean(get(row, "isActive", "IsActive", true)),
      ),
    [sortedTemplates],
  );

  const inactiveTemplates = useMemo(
    () =>
      sortedTemplates.filter(
        (row) => !Boolean(get(row, "isActive", "IsActive", true)),
      ),
    [sortedTemplates],
  );

  const saveTemplate = async (payload) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await saveEpoMailTemplate(payload);

      setDialogOpen(false);
      setEditingTemplate(null);

      await loadTemplates();

      setSuccess("EPO email template saved successfully.");
    } catch (saveError) {
      setError(
        getErrorMessage(saveError, "Unable to save EPO email template."),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleTemplate = async (row) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const id = get(row, "id", "Id", null);

      const currentActive = Boolean(get(row, "isActive", "IsActive", true));

      await setEpoMailTemplateActive(id, !currentActive);

      await loadTemplates();

      setSuccess(
        !currentActive ? "Template activated." : "Template deactivated.",
      );
    } catch (toggleError) {
      setError(getErrorMessage(toggleError));
    } finally {
      setSaving(false);
    }
  };

  const saveTestSettings = async () => {
    try {
      if (testMode && !normalizeText(testToEmails)) {
        setError("Test email is required while Test Mode is enabled.");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      await setEpoMailTestMode({
        isTestMode: testMode,
        testToEmails,
        testCcEmails: testCcEmails || null,
        testBccEmails: null,
      });

      await loadTemplates();

      setSuccess(
        testMode ? "Mail Test Mode enabled." : "Mail Test Mode disabled.",
      );
    } catch (settingsError) {
      setError(getErrorMessage(settingsError));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "grid",
          placeItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {success ? (
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}

      <Alert severity="warning">
        Approval recipient selection is protected. Email templates control
        subject/body/layout, but the actual approval recipient will be resolved
        from the EPO workflow at runtime.
      </Alert>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography variant="h6">Mail Test Mode</Typography>

              <Typography variant="body2" color="text.secondary">
                During development, logical recipients are preserved but
                physical email delivery can be redirected to a test mailbox.
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={testMode}
                  onChange={(event) => setTestMode(event.target.checked)}
                />
              }
              label={testMode ? "Test Mode ON" : "Test Mode OFF"}
            />
          </Stack>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={4.5}>
              <TextField
                fullWidth
                size="small"
                disabled={!testMode}
                label="Test TO Emails"
                value={testToEmails}
                onChange={(event) => setTestToEmails(event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4.5}>
              <TextField
                fullWidth
                size="small"
                disabled={!testMode}
                label="Test CC Emails"
                value={testCcEmails}
                onChange={(event) => setTestCcEmails(event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                size="small"
                variant="contained"
                disabled={saving}
                onClick={saveTestSettings}
                sx={{
                  minHeight: 40,
                }}
              >
                Save Test Mode
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Stack>
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            spacing={1}
            sx={{
              p: 1.5,
            }}
          >
            <Box>
              <Typography variant="h6">Active EPO Email Templates</Typography>

              <Typography variant="body2" color="text.secondary">
                {activeTemplates.length} active template
                {activeTemplates.length === 1 ? "" : "s"}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={saving}
                onClick={() => {
                  setEditingTemplate(null);

                  setDialogOpen(true);
                }}
              >
                Add Template
              </Button>
            </Stack>
          </Stack>

          <Divider />

          <TableContainer
            sx={{
              maxHeight: 350,
              overflowY: "auto",
              overflowX: "auto",

              "&::-webkit-scrollbar": {
                width: 7,
                height: 7,
              },

              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: 4,
              },

              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "rgba(0,0,0,0.3)",
              },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>

                  <TableCell>Event</TableCell>

                  <TableCell>Rule</TableCell>

                  <TableCell>Step</TableCell>

                  <TableCell>Subject</TableCell>

                  <TableCell align="center">PDF</TableCell>

                  <TableCell align="center">Priority</TableCell>

                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {activeTemplates.map((row) => {
                  const id = get(row, "id", "Id", null);

                  return (
                    <TableRow key={id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {get(row, "templateName", "TemplateName", "")}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {get(row, "templateCode", "TemplateCode", "")}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={get(row, "eventCode", "EventCode", "")}
                        />
                      </TableCell>

                      <TableCell>
                        {get(
                          row,
                          "workflowRuleCode",
                          "WorkflowRuleCode",
                          null,
                        ) || (
                          <Typography variant="caption" color="text.secondary">
                            All
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {get(
                          row,
                          "workflowStepCode",
                          "WorkflowStepCode",
                          null,
                        ) || (
                          <Typography variant="caption" color="text.secondary">
                            All
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell
                        sx={{
                          maxWidth: 330,
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          {get(row, "subjectTemplate", "SubjectTemplate", "")}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {Boolean(get(row, "includePdf", "IncludePdf", false))
                          ? "Yes"
                          : "No"}
                      </TableCell>

                      <TableCell align="center">
                        {get(row, "priority", "Priority", 100)}
                      </TableCell>

                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.25}
                          justifyContent="flex-end"
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingTemplate(row);

                                setDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={saving}
                              onClick={() => toggleTemplate(row)}
                            >
                              <PowerSettingsNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!sortedTemplates.length ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography
                        color="text.secondary"
                        sx={{
                          py: 4,
                        }}
                      >
                        No EPO email templates found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        {inactiveTemplates.length > 0 ? (
          <Accordion
            expanded={inactiveExpanded}
            onChange={(_, expanded) => setInactiveExpanded(expanded)}
            variant="outlined"
            disableGutters
            sx={{
              mt: 2,
              backgroundColor: "#F8F8F8",
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                "&.Mui-expanded": {
                  minHeight: 48,
                },
                "& .MuiAccordionSummary-content": {
                  my: 1,
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  my: 1,
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700} color="primary.main">
                  Inactive EPO Email Templates
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                These email templates are inactive and are not used for EPO
                email notifications.
              </Typography>

              <TableContainer
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Template</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell align="center">PDF</TableCell>
                      <TableCell align="center">Priority</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {inactiveTemplates.map((row) => {
                      const id = get(row, "id", "Id", null);

                      return (
                        <TableRow key={id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {get(row, "templateName", "TemplateName", "")}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {get(row, "templateCode", "TemplateCode", "")}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={get(row, "eventCode", "EventCode", "")}
                            />
                          </TableCell>

                          <TableCell>
                            {get(
                              row,
                              "workflowRuleCode",
                              "WorkflowRuleCode",
                              null,
                            ) || (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                All
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {get(
                              row,
                              "workflowStepCode",
                              "WorkflowStepCode",
                              null,
                            ) || (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                All
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell
                            sx={{
                              maxWidth: 330,
                            }}
                          >
                            <Typography variant="body2" noWrap>
                              {get(
                                row,
                                "subjectTemplate",
                                "SubjectTemplate",
                                "",
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {Boolean(
                              get(row, "includePdf", "IncludePdf", false),
                            )
                              ? "Yes"
                              : "No"}
                          </TableCell>

                          <TableCell align="center">
                            {get(row, "priority", "Priority", 100)}
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.25}
                              justifyContent="flex-end"
                            >
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingTemplate(row);
                                    setDialogOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Activate">
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={saving}
                                  onClick={() => toggleTemplate(row)}
                                >
                                  <PowerSettingsNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Paper>

      <TemplateDialog
        open={dialogOpen}
        template={editingTemplate}
        rules={rules}
        employees={employees}
        saving={saving}
        availableTokens={availableTokens}
        onClose={() => {
          setDialogOpen(false);
          setEditingTemplate(null);
        }}
        onSave={saveTemplate}
      />
    </Stack>
  );
};

export default EpoMailTemplateManager;
